import { useExamCenter } from '@exam-center/admin-exam-center-details';
import {
  useAssignExamCoursesToExamCenterMutation,
  AdminSearchExamCenterCoursesDocument,
  SearchExamCenterCoursesDocument,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ExamCenterCourseAssignmentList } from '../exam-center-course-assignment-list';

const AssignExamCourseSchema = z.object({
  examCourses: z.array(z.string()).min(1),
});
type AssignExamCourseFormInput = z.infer<typeof AssignExamCourseSchema>;

interface AssignExamCourseModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AssignExamCourseModal(props: Readonly<AssignExamCourseModalProps>) {
  const { open: isOpen, onClose } = props;

  const { examCenter } = useExamCenter();
  const { code: examCenterCode } = examCenter;

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formContext = useForm<AssignExamCourseFormInput>({
    defaultValues: {
      examCourses: [],
    },
    resolver: zodResolver(AssignExamCourseSchema),
  });

  const [assignExamCoursesToExamCenter] = useAssignExamCoursesToExamCenterMutation();

  const {
    formState: { isSubmitting },
  } = formContext;

  const handleAssignExamCourses = async (data: AssignExamCourseFormInput) => {
    const { examCourses } = data;

    await assignExamCoursesToExamCenter({
      variables: {
        assignExamCoursesToExamCenterInput: {
          examCenterCode,
          examCourseIds: examCourses,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'เพิ่มหลักสูตรเรียบร้อยแล้ว',
          variant: 'success',
        });

        navigate('/admin/exam-centers/:examCenterCode/details/exam-courses', {
          params: {
            examCenterCode,
          },
          replace: true,
        });

        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ไม่สามารถเพิ่มหลักสูตรได้',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [AdminSearchExamCenterCoursesDocument, SearchExamCenterCoursesDocument],
    });
  };

  const handleSelectExamCourses = (examCourses: string[]) => {
    formContext.setValue('examCourses', examCourses, { shouldDirty: true });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='lg'
        slotProps={{
          paper: { component: Form, onSubmit: formContext.handleSubmit(handleAssignExamCourses) },
          transition: { onExited: () => formContext.reset() },
        }}
      >
        <DialogTitle onClose={onClose} loading={isSubmitting}>
          เพิ่มหลักสูตร
        </DialogTitle>
        <Divider />
        <DialogContent>
          <ExamCenterCourseAssignmentList onSelectRow={handleSelectExamCourses} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <FormButton label='เพิ่มหลักสูตร' size='large' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
